import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Chip,
  Avatar,
  Button,
  LinearProgress,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Folder as FolderIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import teamService, { TeamDetails, TeamMember, TeamProject } from '../services/teamService';
import { taskService } from '../services/taskService';
import { format } from 'date-fns';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const TeamDetailsPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [team, setTeam] = useState<TeamDetails | null>(null);
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (teamId) {
      loadData();
    }
  }, [teamId]);

  const loadData = async () => {
    if (!teamId) return;
    try {
      setLoading(true);
      const teamData = await teamService.getTeamById(teamId);
      setTeam(teamData);

      // Fetch tasks for all team members
      if (teamData.members && teamData.members.length > 0) {
        const memberTaskPromises = teamData.members.map((m) =>
          taskService.getTasks({ assigneeId: m.userId }).catch(() => [])
        );
        const allMemberTasks = await Promise.all(memberTaskPromises);
        // Flatten and deduplicate by task ID
        const taskMap = new Map<string, any>();
        allMemberTasks.flat().forEach((t: any) => {
          if (t && t.id) taskMap.set(t.id, t);
        });
        setTasks(Array.from(taskMap.values()));
      }
    } catch (error) {
      console.error('Failed to load team data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !team) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <LinearProgress />
      </Container>
    );
  }

  const completedTasks = tasks.filter((t: any) => t.taskStatus?.category === 'done').length;
  const inProgressTasks = tasks.filter((t: any) => t.taskStatus?.category === 'in_progress').length;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/teams')} sx={{ mb: 2 }}>
          Back to Teams
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Avatar sx={{ width: 80, height: 80, fontSize: '2rem', bgcolor: 'secondary.main' }}>
            <GroupIcon sx={{ fontSize: '2.5rem' }} />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1">{team.name}</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
              {team.leaderName && (
                <Chip icon={<PersonIcon />} label={`Lead: ${team.leaderName}`} size="small" variant="outlined" />
              )}
              <Chip label={`${team.members?.length || 0} members`} size="small" />
              <Chip label={`${team.projects?.length || 0} projects`} size="small" />
            </Box>
          </Box>
        </Box>
        {team.description && (
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2, ml: 12.5 }}>
            {team.description}
          </Typography>
        )}
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Members</Typography>
            <Typography variant="h4">{team.members?.length || 0}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Projects</Typography>
            <Typography variant="h4">{team.projects?.length || 0}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Total Tasks</Typography>
            <Typography variant="h4">{tasks.length}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Completed</Typography>
            <Typography variant="h4" color="success.main">{completedTasks}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="Overview" />
          <Tab icon={<PersonIcon />} iconPosition="start" label="Members" />
          <Tab icon={<FolderIcon />} iconPosition="start" label="Projects" />
          <Tab icon={<AssignmentIcon />} iconPosition="start" label="Tasks" />
        </Tabs>

        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">Team Name</Typography>
                <Typography variant="body1">{team.name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">Leader</Typography>
                <Typography variant="body1">{team.leaderName || 'No leader assigned'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" display="block">Description</Typography>
                <Typography variant="body1">{team.description || 'No description provided.'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">Created</Typography>
                <Typography variant="body1">
                  {team.createdAt ? format(new Date(team.createdAt), 'MMM dd, yyyy') : 'Unknown'}
                </Typography>
              </Grid>
              {team.updatedAt && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Last Updated</Typography>
                  <Typography variant="body1">
                    {format(new Date(team.updatedAt), 'MMM dd, yyyy')}
                  </Typography>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Quick summary */}
            <Typography variant="h6" gutterBottom>Task Summary</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip label={`${tasks.length} Total`} />
              <Chip label={`${completedTasks} Done`} color="success" />
              <Chip label={`${inProgressTasks} In Progress`} color="info" />
              <Chip label={`${tasks.length - completedTasks - inProgressTasks} To Do`} color="default" />
            </Box>
          </Box>
        </TabPanel>

        {/* Members Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Team Members ({team.members?.length || 0})
            </Typography>
            <List>
              {team.members?.map((member: TeamMember) => (
                <React.Fragment key={member.id}>
                  <ListItem
                    button
                    onClick={() => navigate(`/users/${member.userId}`)}
                  >
                    <ListItemAvatar>
                      <Avatar src={member.userProfilePicture || undefined}>
                        {member.userFullName?.[0] || 'U'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={member.userFullName}
                      secondary={
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                          <Typography variant="caption">{member.userEmail}</Typography>
                          <Chip label={member.role} size="small" variant="outlined" />
                          <Chip
                            label={member.isActive ? 'Active' : 'Inactive'}
                            size="small"
                            color={member.isActive ? 'success' : 'default'}
                          />
                          {member.joinedAt && (
                            <Typography variant="caption" color="text.secondary">
                              Joined {format(new Date(member.joinedAt), 'MMM dd, yyyy')}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
              {(!team.members || team.members.length === 0) && (
                <ListItem>
                  <ListItemText primary="No members" secondary="Add members from the Teams page" />
                </ListItem>
              )}
            </List>
          </Box>
        </TabPanel>

        {/* Projects Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Assigned Projects ({team.projects?.length || 0})
            </Typography>
            <List>
              {team.projects?.map((project: TeamProject) => (
                <React.Fragment key={project.projectId}>
                  <ListItem
                    button
                    onClick={() => navigate(`/projects/${project.projectId}`)}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <FolderIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={project.projectName}
                      secondary={
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                          <Chip label={project.projectKey} size="small" variant="outlined" />
                          {project.assignedAt && (
                            <Typography variant="caption" color="text.secondary">
                              Assigned {format(new Date(project.assignedAt), 'MMM dd, yyyy')}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
              {(!team.projects || team.projects.length === 0) && (
                <ListItem>
                  <ListItemText primary="No projects assigned" secondary="Assign this team to a project from the Project page" />
                </ListItem>
              )}
            </List>
          </Box>
        </TabPanel>

        {/* Tasks Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Team Tasks ({tasks.length})
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Assignee</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Due Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks.map((task: any) => (
                    <TableRow
                      key={task.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/tasks/${task.id}`)}
                    >
                      <TableCell>{task.title}</TableCell>
                      <TableCell>
                        {task.assignee
                          ? `${task.assignee.firstName} ${task.assignee.lastName}`
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip label={task.projectName || task.projectKey || '-'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={task.taskStatus?.name || 'Unknown'}
                          size="small"
                          sx={{
                            bgcolor: task.taskStatus?.color || '#6B7280',
                            color: '#fff',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const p = String(task.priority);
                          const labels: Record<string, string> = { '1': 'Low', '2': 'Medium', '3': 'High', '4': 'Urgent' };
                          const colors: Record<string, string> = { '1': '#10B981', '2': '#3B82F6', '3': '#F59E0B', '4': '#EF4444' };
                          return <Chip label={labels[p] || p} size="small" sx={{ bgcolor: colors[p] || '#6B7280', color: '#fff' }} />;
                        })()}
                      </TableCell>
                      <TableCell>
                        {task.dueDate ? format(new Date(task.dueDate), 'MMM dd, yyyy') : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {tasks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                          No tasks assigned to team members
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default TeamDetailsPage;
